/**
 * Tela das casas e das pessoas da família.
 *
 * Papel: junta as duas seções que `colaboradores`, `pedidos_compra`,
 * `documentos_pessoais` e `planos_saude` referenciam — as residências e as
 * pessoas da família —, cada uma com a sua tabela, o seu
 * formulário em caixa de diálogo e a sua confirmação de exclusão.
 *
 * Como usar: as duas seções ficam na mesma tela porque somam poucas linhas e
 * mudam com a mesma raridade; separá-las daria duas telas quase vazias e um
 * item a mais na grade de permissões, já que as duas dependem do mesmo nível em
 * `cadastros-base`.
 *
 * Quem tem nível `ver` não recebe `onNovo`, `onEditar` nem `onRemover`, e por
 * isso não vê botão de ação nenhum. Mostrá-los desabilitados ofereceria uma
 * ação que a API recusaria com 403 depois do clique.
 *
 * A exclusão responde 409 quando a linha ainda é apontada por outro cadastro, e
 * essa mensagem aparece dentro da própria caixa de confirmação: fechá-la antes
 * da resposta esconderia justamente a explicação de por que nada foi apagado.
 */
import { type PessoaFamilia, type Residencia, acessoAlcanca } from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { SecaoCadastro } from '@/shared/ui/secao-cadastro';
import { FormularioPessoaFamilia } from '../components/formulario-pessoa-familia';
import { FormularioResidencia } from '../components/formulario-residencia';
import { TabelaPessoasFamilia } from '../components/tabela-pessoas-familia';
import { TabelaResidencias } from '../components/tabela-residencias';
import {
  useExcluirPessoaFamilia,
  useExcluirResidencia,
  usePessoasFamilia,
  useResidencias,
} from '../hooks/usar-cadastros-base';

/** Em que estado a caixa do formulário está: fechada, criando ou editando algo. */
type PainelAberto =
  | { modo: 'fechado' }
  | { modo: 'nova-residencia' }
  | { modo: 'edicao-residencia'; residencia: Residencia }
  | { modo: 'nova-pessoa' }
  | { modo: 'edicao-pessoa'; pessoa: PessoaFamilia };

/** O que está esperando confirmação de exclusão. */
type AguardandoRemocao =
  { tipo: 'residencia'; residencia: Residencia } | { tipo: 'pessoa'; pessoa: PessoaFamilia };

/**
 * Página de cadastros-base.
 *
 * @returns A tela com as duas seções, a caixa do formulário e a confirmação.
 */
export function PaginaCadastrosBase() {
  const [painel, setPainel] = useState<PainelAberto>({ modo: 'fechado' });
  const [aRemover, setARemover] = useState<AguardandoRemocao | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'cadastros-base', 'editar');

  const consultaResidencias = useResidencias();
  const consultaPessoas = usePessoasFamilia();
  const excluirResidencia = useExcluirResidencia();
  const excluirPessoa = useExcluirPessoaFamilia();

  const removendo = excluirResidencia.isPending || excluirPessoa.isPending;

  /** Fecha a caixa, seja pelo botão, pelo `Esc` ou por clique no véu em volta dela. */
  function handlePainelOpenChange(aberto: boolean) {
    if (!aberto) setPainel({ modo: 'fechado' });
  }

  /** Abre a confirmação de exclusão, limpando a falha da tentativa anterior. */
  function handlePedirRemocao(alvo: AguardandoRemocao) {
    setErroDaRemocao(null);
    setARemover(alvo);
  }

  /** Fecha a confirmação de exclusão, com a falha anterior junto. */
  function handleRemocaoOpenChange(aberto: boolean) {
    if (aberto) return;
    setARemover(null);
    setErroDaRemocao(null);
  }

  /**
   * Apaga o registro confirmado e fecha a caixa.
   *
   * A caixa continua aberta quando a API recusa, com a mensagem dentro: 409
   * significa que o registro ainda é apontado por outro cadastro, e essa é a
   * explicação de por que a linha continua na tabela.
   */
  async function handleConfirmarRemocao() {
    if (aRemover === null) return;
    setErroDaRemocao(null);

    try {
      if (aRemover.tipo === 'residencia') {
        await excluirResidencia.mutateAsync(aRemover.residencia.id);
      } else {
        await excluirPessoa.mutateAsync(aRemover.pessoa.id);
      }
      setARemover(null);
    } catch (erro) {
      setErroDaRemocao(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A exclusão falhou antes de chegar ao servidor.',
      );
    }
  }

  return (
    <div>
      <CabecalhoDoModulo titulo="Cadastros-base" subtitulo="CASAS E PESSOAS DA FAMÍLIA" />

      <SecaoCadastro
        titulo="Residências"
        descricao="As casas administradas pelo escritório, com a cidade de cada uma."
        rotuloNovo="+ Nova residência"
        onNovo={podeEditar ? () => setPainel({ modo: 'nova-residencia' }) : undefined}
        carregando={consultaResidencias.isPending}
        erro={consultaResidencias.error}
        onTentarDeNovo={() => void consultaResidencias.refetch()}
        vazia={(consultaResidencias.data ?? []).length === 0}
        mensagemVazia="Nenhuma residência cadastrada ainda."
      >
        <TabelaResidencias
          residencias={consultaResidencias.data ?? []}
          onEditar={
            podeEditar
              ? (residencia) => setPainel({ modo: 'edicao-residencia', residencia })
              : undefined
          }
          onRemover={
            podeEditar
              ? (residencia) => handlePedirRemocao({ tipo: 'residencia', residencia })
              : undefined
          }
        />
      </SecaoCadastro>

      <SecaoCadastro
        titulo="Pessoas da família"
        descricao="Quem aparece como titular de documento e de plano de saúde."
        rotuloNovo="+ Nova pessoa"
        onNovo={podeEditar ? () => setPainel({ modo: 'nova-pessoa' }) : undefined}
        carregando={consultaPessoas.isPending}
        erro={consultaPessoas.error}
        onTentarDeNovo={() => void consultaPessoas.refetch()}
        vazia={(consultaPessoas.data ?? []).length === 0}
        mensagemVazia="Nenhuma pessoa cadastrada ainda."
      >
        <TabelaPessoasFamilia
          pessoas={consultaPessoas.data ?? []}
          onEditar={
            podeEditar ? (pessoa) => setPainel({ modo: 'edicao-pessoa', pessoa }) : undefined
          }
          onRemover={
            podeEditar ? (pessoa) => handlePedirRemocao({ tipo: 'pessoa', pessoa }) : undefined
          }
        />
      </SecaoCadastro>

      <Dialog open={painel.modo !== 'fechado'} onOpenChange={handlePainelOpenChange}>
        <DialogContent>
          {painel.modo !== 'fechado' && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {painel.modo === 'nova-residencia' && 'Nova residência'}
                  {painel.modo === 'edicao-residencia' && painel.residencia.nome}
                  {painel.modo === 'nova-pessoa' && 'Nova pessoa'}
                  {painel.modo === 'edicao-pessoa' && painel.pessoa.nomeCompleto}
                </DialogTitle>
              </DialogHeader>

              {(painel.modo === 'nova-residencia' || painel.modo === 'edicao-residencia') && (
                <FormularioResidencia
                  key={painel.modo === 'nova-residencia' ? 'nova' : painel.residencia.id}
                  residencia={painel.modo === 'nova-residencia' ? null : painel.residencia}
                  onSalvo={() => setPainel({ modo: 'fechado' })}
                  onCancelar={() => setPainel({ modo: 'fechado' })}
                />
              )}

              {(painel.modo === 'nova-pessoa' || painel.modo === 'edicao-pessoa') && (
                <FormularioPessoaFamilia
                  key={painel.modo === 'nova-pessoa' ? 'nova' : painel.pessoa.id}
                  pessoa={painel.modo === 'nova-pessoa' ? null : painel.pessoa}
                  onSalvo={() => setPainel({ modo: 'fechado' })}
                  onCancelar={() => setPainel({ modo: 'fechado' })}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={aRemover !== null} onOpenChange={handleRemocaoOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remover{' '}
              {aRemover?.tipo === 'residencia'
                ? aRemover.residencia.nome
                : aRemover?.pessoa.nomeCompleto}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {aRemover?.tipo === 'residencia'
                ? 'A casa sai do cadastro e deixa de aparecer nas listas dos outros módulos. Não há como desfazer.'
                : 'A pessoa sai do cadastro e deixa de aparecer nas listas dos outros módulos. Não há como desfazer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {erroDaRemocao !== null && (
            <p className="text-sm text-destructive" role="alert">
              {erroDaRemocao}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={removendo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={removendo}
              onClick={(evento) => {
                // O `AlertDialogAction` fecha a caixa ao ser clicado, e fechá-la
                // antes da resposta da API desmontaria o estado de envio e
                // esconderia a recusa por registro em uso. O fechamento passa a
                // ser nosso, depois de a exclusão confirmar.
                evento.preventDefault();
                void handleConfirmarRemocao();
              }}
            >
              {removendo ? 'Removendo…' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
